"use client";

import {
  ArchiveIcon,
  EyeIcon,
  HashIcon,
  MailIcon,
  MailOpenIcon,
  PauseCircleIcon,
  PencilIcon,
  PinIcon,
  PinOffIcon,
  PlayCircleIcon,
  Trash2Icon,
  XCircleIcon,
} from "lucide-react";
import Link from "next/link";

import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import type {
  WorkspaceChannel,
  WorkspaceSession,
} from "@/lib/mlx-workspace-client";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  ProvenanceAvatarStack,
  channelClientProvenance,
  clientAppLabel,
  interfaceLabel,
  sessionClientProvenance,
  type ProvenanceActor,
} from "@/components/app/runs/provenance-avatar-stack";
import {
  workspaceSessionActionAvailability,
  workspaceSessionTitle,
} from "./workspace-navigation-model";
import { LiveSessionTitle } from "@/components/app/sessions/live-session-title";

export type SessionNavigationImmediateAction = "pause" | "resume" | "cancel";

const NAVIGATION_STATUS_DOT_CLASS = "size-2 shrink-0 rounded-full";

function navigationRowState(active: boolean) {
  return active
    ? "border-transparent bg-primary/[0.08] text-primary"
    : "border-transparent hover:border-primary/20 hover:bg-primary/10 hover:text-primary";
}

export function SessionNavigationRow(props: {
  active: boolean;
  pending: boolean;
  pinned: boolean;
  unread: boolean;
  session: WorkspaceSession;
  provenanceActor?: ProvenanceActor | null;
  snippet?: string;
  onArchive: () => void;
  onDelete: () => void;
  onPinChange: () => void;
  onReadChange: () => void;
  onRename: () => void;
  onSessionAction: (action: SessionNavigationImmediateAction) => void;
}) {
  const href = `/workspace/sessions/${encodeURIComponent(props.session.id)}`;
  const availability = workspaceSessionActionAvailability(props.session.status);
  const title = workspaceSessionTitle(props.session);
  const provenance = sessionClientProvenance(props.session.metadata);
  return (
    <ContextMenu disabled={props.pending}>
      <ContextMenuTrigger
        render={
          <div
            className={cn(
              "group relative mx-1 rounded-md border text-sm leading-[18px] transition-colors",
              navigationRowState(props.active),
              props.pending && "pointer-events-none opacity-55",
            )}
          />
        }
      >
        <TooltipProvider delay={300}>
          <Tooltip>
            <TooltipTrigger
              render={
                <Link
                  aria-current={props.active ? "page" : undefined}
                  className={cn(
                    "flex h-[30px] min-w-0 items-center gap-2 pl-2 pr-2",
                    props.session.status === "running" && "pr-8",
                  )}
                  href={href}
                />
              }
            >
              <LiveSessionTitle
                className="min-w-0 flex-1 truncate font-medium"
                title={title}
              />
              <span
                aria-label={props.unread ? "Unread" : "Read"}
                className={cn(
                  NAVIGATION_STATUS_DOT_CLASS,
                  props.unread
                    ? "animate-pulse bg-badge-sky shadow-[0_0_0_3px_rgb(14_165_233/0.12)]"
                    : "bg-muted-foreground/35",
                )}
              />
            </TooltipTrigger>
            <TooltipContent
              side="right"
              sideOffset={10}
              className="block w-72 overflow-hidden rounded-xl border border-border/70 bg-popover p-0 text-popover-foreground shadow-xl shadow-black/10"
            >
              <NavigationEntityPreview
                actor={props.provenanceActor}
                action="started"
                description={props.snippet}
                kind="Session"
                provenance={provenance}
                title={title}
              />
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        {props.session.status === "running" ? (
          <span
            aria-label="Running"
            className="pointer-events-none absolute right-0 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center text-muted-foreground transition-opacity group-hover:opacity-0"
            title="Running"
          >
            <Spinner className="size-3.5" strokeWidth={1.75} />
          </span>
        ) : null}
        <button
          aria-label={`Archive ${title}`}
          className="absolute right-0 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-md bg-background/85 text-muted-foreground opacity-0 shadow-[-6px_0_8px_-4px_hsl(var(--background))] backdrop-blur-md transition-opacity hover:bg-background hover:text-foreground focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring group-hover:opacity-100"
          disabled={props.pending}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            props.onArchive();
          }}
          type="button"
        >
          <ArchiveIcon className="size-3.5" />
        </button>
      </ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem render={<Link href={href} />}>
          <EyeIcon />
          Open
        </ContextMenuItem>
        <ContextMenuItem onClick={props.onPinChange}>
          {props.pinned ? <PinOffIcon /> : <PinIcon />}
          {props.pinned ? "Unpin" : "Pin"}
        </ContextMenuItem>
        <ContextMenuItem onClick={props.onRename}>
          <PencilIcon />
          Rename
        </ContextMenuItem>
        <ContextMenuItem onClick={props.onReadChange}>
          {props.unread ? <MailOpenIcon /> : <MailIcon />}
          {props.unread ? "Mark as read" : "Mark as unread"}
        </ContextMenuItem>
        {availability.canResume ||
        availability.canPause ||
        availability.canCancel ? (
          <ContextMenuSeparator />
        ) : null}
        {availability.canResume ? (
          <ContextMenuItem onClick={() => props.onSessionAction("resume")}>
            <PlayCircleIcon />
            Resume
          </ContextMenuItem>
        ) : null}
        {availability.canPause ? (
          <ContextMenuItem onClick={() => props.onSessionAction("pause")}>
            <PauseCircleIcon />
            Pause
          </ContextMenuItem>
        ) : null}
        {availability.canCancel ? (
          <ContextMenuItem
            onClick={() => props.onSessionAction("cancel")}
            variant="destructive"
          >
            <XCircleIcon />
            Cancel
          </ContextMenuItem>
        ) : null}
        <ContextMenuSeparator />
        <ContextMenuItem onClick={props.onArchive}>
          <ArchiveIcon />
          Archive
        </ContextMenuItem>
        <ContextMenuItem onClick={props.onDelete} variant="destructive">
          <Trash2Icon />
          Delete
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}

function navigationProvenanceText(
  provenance: ReturnType<typeof sessionClientProvenance>,
) {
  const app = clientAppLabel(provenance.clientApp);
  const via = provenance.interfaceType
    ? interfaceLabel(provenance.interfaceType)
    : "web app";
  return app ? `${app} via ${via}` : via;
}

function NavigationEntityPreview(props: {
  actor?: ProvenanceActor | null;
  action: "started" | "created";
  description?: string;
  kind: "Session" | "Channel";
  provenance: ReturnType<typeof sessionClientProvenance>;
  title: string;
}) {
  const actor = props.actor?.label ?? "Twin";
  return (
    <div>
      <div className="space-y-1.5 px-3.5 pb-3 pt-3.5">
        <p className="text-3xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          {props.kind}
        </p>
        <p className="line-clamp-2 text-sm font-semibold leading-5 text-foreground">
          {props.title}
        </p>
        {props.description ? (
          <p className="line-clamp-2 text-xs leading-[18px] text-muted-foreground">
            {props.description}
          </p>
        ) : null}
      </div>
      <div className="flex items-center gap-2.5 border-t border-border/60 bg-muted/25 px-3.5 py-2.5">
        <ProvenanceAvatarStack
          actor={props.actor}
          {...props.provenance}
          action={props.action}
          layout="overlay"
          size="md"
        />
        <div className="min-w-0 leading-tight">
          <p className="truncate text-xs font-medium text-foreground">
            {props.action === "started" ? "Started" : "Created"} by {actor}
          </p>
          <p className="mt-1 truncate text-2xs text-muted-foreground">
            {navigationProvenanceText(props.provenance)}
          </p>
        </div>
      </div>
    </div>
  );
}

export function ChannelNavigationRow(props: {
  active: boolean;
  pending: boolean;
  pinned: boolean;
  running: boolean;
  channel: WorkspaceChannel;
  provenanceActor?: ProvenanceActor | null;
  onArchive: () => void;
  onPinChange: () => void;
  onRename: () => void;
}) {
  const href = `/workspace/channels/${encodeURIComponent(props.channel.id)}`;
  const provenance = channelClientProvenance(props.channel.metadata);
  return (
    <ContextMenu disabled={props.pending}>
      <ContextMenuTrigger
        render={
          <div
            className={cn(
              "group relative mx-1 rounded-md border text-sm leading-[18px] transition-colors",
              navigationRowState(props.active),
              props.pending && "pointer-events-none opacity-55",
            )}
          />
        }
      >
        <TooltipProvider delay={300}>
          <Tooltip>
            <TooltipTrigger
              render={
                <Link
                  aria-current={props.active ? "page" : undefined}
                  className="flex h-[30px] min-w-0 items-center gap-2 pl-2 pr-2"
                  href={href}
                />
              }
            >
              <HashIcon className="size-3.5 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate font-medium">
                {props.channel.name}
              </span>
              <span
                aria-label={
                  props.running ? "Session running" : "No session running"
                }
                className={cn(
                  NAVIGATION_STATUS_DOT_CLASS,
                  props.running
                    ? "animate-pulse bg-badge-sky"
                    : "bg-muted-foreground/25",
                )}
              />
            </TooltipTrigger>
            <TooltipContent
              side="right"
              sideOffset={10}
              className="block w-72 overflow-hidden rounded-xl border border-border/70 bg-popover p-0 text-popover-foreground shadow-xl shadow-black/10"
            >
              <NavigationEntityPreview
                actor={props.provenanceActor}
                action="created"
                description={props.channel.purpose}
                kind="Channel"
                provenance={provenance}
                title={props.channel.name}
              />
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem render={<Link href={href} />}>
          <EyeIcon />
          Open
        </ContextMenuItem>
        <ContextMenuItem onClick={props.onPinChange}>
          {props.pinned ? <PinOffIcon /> : <PinIcon />}
          {props.pinned ? "Unpin" : "Pin"}
        </ContextMenuItem>
        <ContextMenuItem onClick={props.onRename}>
          <PencilIcon />
          Rename
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem onClick={props.onArchive}>
          <ArchiveIcon />
          Archive
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}
